import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, RotateCcw, AlertTriangle, X, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

function apiFetch(path, token, opts = {}) {
  return fetch(`${API}/api${path}`, {
    ...opts,
    headers: { ...authHeaders(token), ...(opts.headers || {}) },
  });
}

// ── Confirmation Modal ──────────────────────────────────────────────────────

function ConfirmModal({ modal, onConfirm, onCancel, loading }) {
  if (!modal) return null;
  const isDanger = modal.variant === "danger";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      data-testid="confirm-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-start gap-3 mb-4">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: isDanger ? "#fee2e2" : "#fef3c7" }}
          >
            <AlertTriangle size={20} style={{ color: isDanger ? "#ef4444" : "#f59e0b" }} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{modal.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{modal.message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
            data-testid="confirm-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
            style={{ background: isDanger ? "#ef4444" : "#f59e0b", opacity: loading ? 0.7 : 1 }}
            data-testid="confirm-modal-confirm"
          >
            {loading ? "Working..." : modal.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Learner Modal ────────────────────────────────────────────────────

function CreateLearnerModal({ onClose, onCreated, token }) {
  const [name, setName] = useState("");
  const [grade, setGrade] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/admin/cr/learners", token, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), grade: parseInt(grade) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create learner");
      onCreated(data.learner);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      data-testid="create-learner-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-900">Create Test Learner</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Test Student"
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="create-learner-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              data-testid="create-learner-grade"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((g) => (
                <option key={g} value={g}>Grade {g}</option>
              ))}
            </select>
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ background: "#2563eb", opacity: loading || !name.trim() ? 0.6 : 1 }}
              data-testid="create-learner-submit"
            >
              {loading ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Status & Grade badges ───────────────────────────────────────────────────

function StatusBadge({ status }) {
  const styles =
    status === "active"
      ? { background: "#d1fae5", color: "#065f46" }
      : { background: "#e0e7ff", color: "#3730a3" };
  return (
    <span
      className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={styles}
    >
      {status}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "—";
  }
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function LearnerManager({ token, onUnauth }) {
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | test
  const [modal, setModal] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/admin/cr/learners", token);
      if (res.status === 401) { onUnauth(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to load learners");
      setLearners(data.learners);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, onUnauth]);

  useEffect(() => { load(); }, [load]);

  const filtered = learners.filter((l) => {
    if (filter === "active") return l.status === "active";
    if (filter === "test") return l.status === "test";
    return true;
  });

  const stats = {
    total: learners.length,
    active: learners.filter((l) => l.status === "active").length,
    test: learners.filter((l) => l.status === "test").length,
  };

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleDelete = (learner) => {
    setModal({
      type: "delete",
      learner_id: learner.learner_id,
      title: `Delete "${learner.name}"?`,
      message: "This will permanently remove the learner and all their progress data across all collections. This cannot be undone.",
      confirmLabel: "Delete Learner",
      variant: "danger",
    });
  };

  const handleReset = (learner) => {
    setModal({
      type: "reset",
      learner_id: learner.learner_id,
      title: `Reset "${learner.name}"?`,
      message: "All progress, sessions, attempts, and skill states will be wiped. The learner profile will be kept.",
      confirmLabel: "Reset Progress",
      variant: "warning",
    });
  };

  const handleBulkDelete = () => {
    setModal({
      type: "bulk_delete",
      title: `Delete ${stats.test} test learner${stats.test !== 1 ? "s" : ""}?`,
      message: `All learners flagged as "test" will be permanently deleted, along with all their data. This cannot be undone.`,
      confirmLabel: `Delete ${stats.test} Test Learners`,
      variant: "danger",
    });
  };

  const executeModal = async () => {
    if (!modal) return;
    setModalLoading(true);
    try {
      let res;
      if (modal.type === "delete") {
        res = await apiFetch(`/admin/cr/learners/${modal.learner_id}`, token, { method: "DELETE" });
      } else if (modal.type === "reset") {
        res = await apiFetch(`/admin/cr/learners/${modal.learner_id}/reset`, token, { method: "POST" });
      } else if (modal.type === "bulk_delete") {
        res = await apiFetch("/admin/cr/learners/bulk-test", token, { method: "DELETE" });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Action failed");
      setActionResult({ success: true, message: data.ok ? `Done. ${modal.type === "reset" ? "Progress reset." : "Deleted."}` : "Completed" });
      setModal(null);
      await load();
    } catch (err) {
      setActionResult({ success: false, message: err.message });
      setModal(null);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="learner-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Learner Manager</h1>
          <p className="text-sm text-gray-500 mt-1">
            {stats.total} total · {stats.active} active · {stats.test} test
          </p>
        </div>
        <div className="flex items-center gap-3">
          {stats.test > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-red-700 border border-red-200 hover:bg-red-50 transition-colors"
              data-testid="bulk-delete-test-btn"
            >
              <Trash2 size={15} />
              Delete All Test ({stats.test})
            </button>
          )}
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
            style={{ background: "#2563eb" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#1d4ed8")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#2563eb")}
            data-testid="create-learner-btn"
          >
            <Plus size={15} />
            Create Test Learner
          </button>
        </div>
      </div>

      {/* Action result toast */}
      {actionResult && (
        <div
          className="mb-4 flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium"
          style={{
            background: actionResult.success ? "#d1fae5" : "#fee2e2",
            color: actionResult.success ? "#065f46" : "#991b1b",
          }}
          data-testid="action-result"
        >
          {actionResult.message}
          <button onClick={() => setActionResult(null)}>
            <X size={15} />
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 bg-white border border-gray-200 rounded-xl p-1 w-fit">
        {["all", "active", "test"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors"
            style={{
              background: filter === f ? "#111827" : "transparent",
              color: filter === f ? "#fff" : "#6b7280",
            }}
            data-testid={`filter-${f}`}
          >
            {f === "all" ? `All (${stats.total})` : f === "active" ? `Active (${stats.active})` : `Test (${stats.test})`}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-gray-400 py-12 text-center">Loading...</div>
      ) : error ? (
        <div className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">{error}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="learner-table">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {["Name", "Grade", "Status", "Created", "Last Active", "Actions"].map((h) => (
                  <th
                    key={h}
                    className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    No learners found
                  </td>
                </tr>
              ) : (
                filtered.map((learner) => (
                  <tr
                    key={learner.learner_id}
                    className="hover:bg-gray-50 transition-colors"
                    style={{ borderBottom: "1px solid #f3f4f6" }}
                    data-testid={`learner-row-${learner.learner_id}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-gray-900">{learner.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{learner.learner_id}</div>
                    </td>
                    <td className="px-5 py-3.5 text-gray-700">
                      G{learner.grade}
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge status={learner.status} />
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">{formatDate(learner.created_at)}</td>
                    <td className="px-5 py-3.5 text-gray-500">{formatDate(learner.last_active)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleReset(learner)}
                          title="Reset progress"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors"
                          data-testid={`reset-learner-${learner.learner_id}`}
                        >
                          <RotateCcw size={12} />
                          Reset
                        </button>
                        <button
                          onClick={() => handleDelete(learner)}
                          title="Delete learner"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                          data-testid={`delete-learner-${learner.learner_id}`}
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <ConfirmModal
        modal={modal}
        onConfirm={executeModal}
        onCancel={() => setModal(null)}
        loading={modalLoading}
      />
      {showCreate && (
        <CreateLearnerModal
          token={token}
          onClose={() => setShowCreate(false)}
          onCreated={(newLearner) => {
            setLearners((prev) => [newLearner, ...prev]);
            setShowCreate(false);
            setActionResult({ success: true, message: `Created learner "${newLearner.name}" (${newLearner.learner_id})` });
          }}
        />
      )}
    </div>
  );
}
